/**
 * Payment Helper Utilities
 * Shared functions for payment processing and display
 */

// Payment narrations that represent actual cash outflows to investors
export const PAYMENT_NARRATIONS = [
  'Interest Payment',
  'Capital Payment',
  'Adhoc Capital Withdrawal',
];

/**
 * Check if a narration represents an actual payment (cash outflow)
 * @param {string} narration - The narration text from amortization schedule
 * @returns {boolean} True if this is a payment narration
 */
export const isPaymentNarration = (narration) => {
  if (!narration) return false;
  return PAYMENT_NARRATIONS.some(n => narration.includes(n));
};

/**
 * Calculate the difference in days between two dates
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {number} Absolute difference in days
 */
export const differenceInDays = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  d1.setHours(0, 0, 0, 0);
  d2.setHours(0, 0, 0, 0);
  const diffTime = Math.abs(d1 - d2);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Add days to a date
 * @param {Date} date - Starting date
 * @param {number} days - Number of days to add
 * @returns {Date} New date with days added
 */
export const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Get payment status color for MUI Chip
 * @param {string} status - Payment status
 * @returns {string} MUI color name
 */
export const getPaymentStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'paid':
    case 'matched':
    case 'received':
      return 'success';
    case 'overdue':
    case 'rejected':
    case 'failed':
      return 'error';
    case 'pending':
    case 'partial':
      return 'warning';
    default:
      return 'default';
  }
};

/**
 * Get payment type color for MUI Chip
 * @param {string} type - Payment type or narration
 * @returns {string} MUI color name
 */
export const getPaymentTypeColor = (type) => {
  if (!type) return 'default';
  if (type.includes('Interest')) return 'info';
  if (type.includes('Capital')) return 'success';
  if (type.includes('Settlement')) return 'warning';
  if (type.includes('Withdrawal')) return 'warning';
  return 'default';
};
